import type { SavedDestination } from "@solomon-apps/shared";
import { formatAddressTier } from "@solomon-apps/shared/src/components/ResponsiveAddress";

import { isSettledInvalidAddress, isValidSolanaAddress, looksLikeExchangeAddress } from "@/pages/shared/format";

/** Route of the in-page add flow (a mode of the Reward Destinations page). */
export const ADD_DESTINATION_PATH = "/reward-destinations/add";
/** Where the add flow returns to — on success, Cancel, or the back link. */
export const REWARD_DESTINATIONS_PATH = "/reward-destinations";

export interface AddDestinationFormValues {
    /** Solana address, already trimmed by the field. */
    address: string;
    /** Optional free-text label. */
    label: string;
    /** The address field has blurred at least once. */
    addressTouched: boolean;
}

export interface AddDestinationFormCheck {
    /** The address is a valid Solana address — gates Save. */
    valid: boolean;
    /** Show the Invalid Address badge: while typing once it is surely wrong,
     *  and after blur for anything typed that is not an address. */
    showInvalid: boolean;
    /** Valid, but looks like a known exchange deposit address. */
    warnExchange: boolean;
}

/** Validation state for the add-destination form. Pure. */
export function checkAddDestinationForm({
    address,
    addressTouched
}: AddDestinationFormValues): AddDestinationFormCheck {
    const valid = isValidSolanaAddress(address);
    return {
        valid,
        // As it is typed: once the address is known to be wrong (a
        // character base58 never uses, or long enough to be one and still
        // not a key), or once the field is left with it unfinished.
        showInvalid: isSettledInvalidAddress(address) || (addressTouched && address.length > 0 && !valid),
        warnExchange: valid && looksLikeExchangeAddress(address)
    };
}

/**
 * The create payload. `name` falls back to the address when no label is
 * given — the "no label" convention shared with `SavedDestinationsCard` and
 * read back by `savedDestinationLabel` below.
 */
export function toSavedDestinationInput(address: string, label: string): { name: string; address: string } {
    return { name: label.trim() || address, address };
}

/**
 * The custom label of a saved destination, or "" when it has none. A
 * destination saved without a label stores `name === address` (see
 * `toSavedDestinationInput`).
 */
export function savedDestinationLabel(destination: SavedDestination): string {
    const trimmed = destination.name.trim();
    return trimmed === "" || trimmed === destination.address ? "" : trimmed;
}

/**
 * The default rewards destination's name: nothing picked means rewards go to
 * the position source. The picker's first option and the receipt's name.
 */
export const SAME_AS_SOURCE_LABEL = "Same as Position Source";

/**
 * What a destination is called wherever it is named (the Rewards
 * Destinations table's Name column, the picker's options and value): its
 * custom label, or, when it has none, its address in the 4 + 4 short form
 * ("9HjD…Gjvj"). Never "Unlabeled".
 */
export function destinationDisplayName(destination: { label: string; address: string }): string {
    return destination.label || formatAddressTier(destination.address, "short");
}

/** `destinationDisplayName` for a saved destination. */
export function savedDestinationDisplayName(destination: SavedDestination): string {
    return destinationDisplayName({
        label: savedDestinationLabel(destination),
        address: destination.address
    });
}

/** Case-insensitive match on a saved destination's label or full address. */
export function matchesDestinationQuery(destination: SavedDestination, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (q === "") return true;
    return (
        savedDestinationLabel(destination).toLowerCase().includes(q) || destination.address.toLowerCase().includes(q)
    );
}

/** The saved destination at `address`, if any. */
export function findSavedDestination(destinations: SavedDestination[], address: string): SavedDestination | undefined {
    const trimmed = address.trim();
    if (trimmed === "") return undefined;
    return destinations.find((d) => d.address === trimmed);
}

export interface TypedDestinationCheck {
    /** The query can be added as a new destination: offer the "Add" row. */
    addable: boolean;
    /** Non-empty but not a valid Solana address: the list says how to add. */
    invalid: boolean;
    /** Addable, but looks like a known exchange deposit address. */
    warnExchange: boolean;
}

/**
 * Whether an address typed into the destination picker may be added as a new
 * saved destination. Strict on purpose: an address field must not let a typo
 * pass as a selection, so the trimmed query has to be a valid Solana address
 * (`checkAddDestinationForm`) that no saved destination's address contains.
 * The containment check (not just equality) also refuses a truncated paste of
 * an address already saved, which can still be valid base58 of legal length.
 */
export function checkTypedDestination(destinations: SavedDestination[], query: string): TypedDestinationCheck {
    const address = query.trim();
    const { valid, showInvalid, warnExchange } = checkAddDestinationForm({
        address,
        label: "",
        addressTouched: true
    });
    const addable = valid && !destinations.some((d) => d.address.includes(address));
    return { addable, invalid: showInvalid, warnExchange: addable && warnExchange };
}
